// shared/utils/format.ts — Platik-IA

/** Formatea un número como moneda argentina */
export function formatCurrency(amount: number): string {
  return amount.toLocaleString('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/** Parsea una fecha que puede venir en ISO 8601 o en "dd/mm/yyyy [hh:mm:ss]"
 *  (formato es-AR del v2). Evita que `new Date()` interprete dd/mm como mm/dd (US). */
export function parseDate(value: string): Date {
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T].*)?$/);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  return new Date(value);
}

/** Formatea una fecha (ISO o dd/mm/yyyy) a string legible */
export function formatDate(value: string): string {
  return parseDate(value).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Devuelve "hoy", "ayer" o la fecha formateada */
export function formatRelativeDate(value: string): string {
  const d    = parseDate(value);
  const now  = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (diff === 0) return 'Hoy';
  if (diff === 1) return 'Ayer';
  return formatDate(value);
}

/** Fecha (ISO o dd/mm/yyyy) → "dd/mm/yyyy" (formato de la planilla). Si no es válida, devuelve el original. */
export function isoToSheetDate(iso: string): string {
  const d = parseDate(iso);
  if (isNaN(d.getTime())) return iso;
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** "dd/mm/yyyy" (con hora opcional, formato es-AR del v2) → ISO 8601 a medianoche
 *  LOCAL (sin sufijo Z, para no correr el día en zonas UTC-negativas).
 *  Se descarta la hora; solo importa la fecha. Si ya viene en ISO u otro formato, lo conserva. */
export function sheetDateToIso(value: string): string {
  const m = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T].*)?$/);
  if (!m) return value;
  const [, dd, mm, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T00:00:00`;
}

/** Genera un UUID v4 compatible sin crypto.randomUUID (PWA offline) */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Trunca texto con ellipsis */
export function truncate(text: string, max: number): string {
  return text.length > max ? text.slice(0, max - 1) + '…' : text;
}

/** Clasifica el período de una fecha ISO */
export function getPeriodDates(period: string): { desde: Date; hasta: Date } {
  const hasta  = new Date();
  const desde  = new Date(hasta);
  switch (period) {
    case 'mes_actual':  desde.setDate(1); desde.setHours(0, 0, 0, 0); break;
    case 'semana':      desde.setDate(hasta.getDate() - 7); break;
    case 'mes':         desde.setMonth(hasta.getMonth() - 1); break;
    case 'trimestre':   desde.setMonth(hasta.getMonth() - 3); break;
    case 'anio':        desde.setFullYear(hasta.getFullYear() - 1); break;
    case 'todo':        desde.setFullYear(2020, 0, 1); break;
  }
  return { desde, hasta };
}
